import { ComponentBase } from "../components/ComponentBase.js";
import { template, unsafeHTML } from '../utils/TemplateParser';
import { Hero } from '../components/Hero';
import { Controls } from '../components/Controls';
import { FeaturedSlider } from '../components/FeaturedSlider';
import { EventsGrid } from '../components/EventsGrid';
import { vars } from '../utils/vars';
import { api } from "../utils/api.js";
import { lang } from '../lang';
import { Emitters } from '../utils/Emitters';
import { formatStartDate, formatLocation } from '../utils';
import { DateTime } from 'luxon';
import placeholderImage from '../../images/event-placeholder.png';

export class EventsList extends ComponentBase {
    constructor({filters, tag, query}) {
        super();
        this.hero = new Hero({ tag, query });
        this.hero.render();
        this.defaultLayout = query ? 'LIST' : this.getDefaultLayout();
        this.slider = new FeaturedSlider({ defaultLayout: this.defaultLayout });
        this.controls = new Controls({ filters, tag, query });
        this.eventsGrid = new EventsGrid({listView: this});
        this.eventsGrid.render();
        this.eventsSet = new Set(); // Used to store unique event ID and start date to prevent duplicate multi-day events from displaying if API includes the same event again in subsequent queries. 
        this.maxQueryDate = DateTime.local().plus({days: 830}); // 2 years plus one extra 100 day range (365 * 2) + 100

        this.resetCurrentDateRange();

        this.initFetch();
    }

    registerEvents() {
        return {
            'click  [data-action="show-grid"]'  : 'showGrid',
            'click  [data-action="show-list"]'  : 'showList',
            'click .load-more-events'           : 'loadNextBatch',
            'filterChange'                      : 'handleFilterChange',
            'click a[data-action="routeChange"]': 'handleRouteChange',
        };
    }

    showGrid(e) {
        this.eventsGrid.fullCalendar?.today();
        this.resetCurrentDateRange();
        this.eventsGrid.loadMonth();    // Load initial month

        this.elements.eventWrapper.classList.add('hide');
        this.slider.hide();
        this.elements.gridWrapper.classList.remove('hide');
        this.elements.loadEventsWrapper.classList.add('hide');
        this.setBtnActive('grid');
        this.eventsGrid.fullCalendar?.render();  // re-render to try to avoid strange rendering issues
        this.currentView = 'grid';
    }
    showList(e, useGridDates = false) {
        const gridDate = this.eventsGrid.fullCalendar?.getDate();
        this.resetCurrentDateRange();
        // Render the same month user was viewing on grid view
        if (useGridDates && gridDate) {
            this.currentDateRange.start = DateTime.fromJSDate(gridDate).startOf('month').toISODate();
            this.currentDateRange.end = DateTime.fromJSDate(gridDate).endOf('month').toISODate();
        }
        this.refetch().then(() => {
            if(!this.collection.length) this.loadNextBatch(); // Automatically extend date range if no events found in first 30 days.
        });
        
        this.elements.eventWrapper.classList.remove('hide');
        this.elements.gridWrapper.classList.add('hide');
        this.elements.loadEventsMessage.innerText = '';
        this.elements.loadEventsButton.classList.remove('hide');
        this.elements.loadEventsWrapper.classList.remove('hide');
        this.setBtnActive('list');
        this.currentView = 'list';
    }
    setBtnActive(action = 'list') {
        const btn = this.el.querySelector(`[data-action="show-${action}"]`);
        btn.parentElement.querySelector('[aria-selected="true"]').setAttribute('aria-selected', false);
        btn.setAttribute('aria-selected', true);
    }

    template() {
        return template`
            ${this.hero}
            <div class="wrapper">
                <div role="region" class="page" aria-labelledby="page-title">
                    ${this.controls}
                    ${this.slider}
                    <div class="event-wrapper">
                        ${this.eventsTemplate()}
                    </div>
                    <div class='grid-wrapper'>
                        ${this.eventsGrid}
                    </div>
                    <div class="load-more-events-wrapper">
                        <div class="load-more-events-message"></div>
                        <button class="load-more-events">${lang.LoadMoreEvents}</button>
                    </div>
                    ${this.footerTemplate()}
                </div>
            </div>
        `;
    }

    eventsTemplate() {
        if (this.collection.length > 0) {
            return this.collection.map(event => {
                return this.eventTemplate(event)
            });
        } else {
            const query = this.controls.getFilterOptions().query;
            if(query) {
                return template`<p>${lang.NoResultsFor.replace('{{query}}', query)}</p>`;
            } else {
                return template`<p>${lang.NoEventsFound}</p>`;
            }
        }
    }

    eventTemplate(event) {
        const filterOptions = this.controls.filterOptions;
        const image = event.image ? event.image : placeholderImage;
        const imageAltText = event.image ? event.imageAltText : lang.PlaceholderImage;
        const location = event.locationOther ? formatLocation(event.locationOther) : event.location;
        const locationTemplate = location || event.locationRoom ? template`<div class="location detail-with-icon" aria-label="${lang.EventLocation}"><i class="icon fas fa-map-marker-alt"></i> <div>${location ?? ''} ${event.locationRoom ?? ''}</div></div>` : '';
        let href = this.getRouteURL(`event-details/${event.id}`);
        if(event.recurring) href += `/instances/${event.startDate ?? event.startDatetime}`;
        
        return template`
            <section data-name="${event.title}" class="event-list" data-event-id="${event.id}" data-calendar-id="${event.category}" data-calendar-name="${event.categoryName}" data-tags="${event.tags.join()}">
                    ${event.featured && !filterOptions.featured ? template`<i class="icon fas fa-solid fa-star featured" aria-label="${lang.FeaturedEvent}"></i>` : ''}
                    <div class="event-list-left" style="background-image: url(${image})" aria-label="${imageAltText}" role="img">
                    </div>
                    <div class="event-list-content">
                        ${this.eventLabelTemplate(event)}
                        <h3 class="event-list-title"><a href="${href}" data-action="routeChange">${event.title}</a></h3>
                        <div class="date detail-with-icon" aria-label="${lang.EventDate}"><i class="icon fas fa-calendar-alt"></i> <div>${formatStartDate(event)}</div></div>
                        ${locationTemplate}
                        ${this.controls.ticketTemplate(event)}
                    </div>
            </section>
        `;
    }

    eventLabelTemplate(event) {
        let status;
        let classes = '';
        switch(event.status) {
            case "POSTPONED":
                status = lang.Postponed;
                classes = 'is-postponed';
                break;
            
            case "CANCELLED":
                status = lang.Cancelled;
                classes = 'is-cancelled';
                break;

            default:
                status = event.categoryName;
        }

        return template`<div class="event-label ${classes}">${status}</div>`;
    }

    footerTemplate() {
        return '';      // NOTE: public event submission isn't launching until phase 2. At that time, remove this empty return.
        return template`
            <div class="footer">
                <p>${lang.SubmitYourEvent}</p>
                <p>${unsafeHTML(lang.FooterSubmitEventMessage)}</p>
            </div>
        `;
    }

    /**
     * Initialize page by fetching data for this page and used components
     */
    initFetch() {
        return Promise.all([
            this.fetch(),
            api.getTags(),
            api.getFeaturedSliderEvents(),
        ])
        .then((data) => {
            this.loadError = false;
            this.controls.tagCollection = data[1];
            this.controls.render();
            this.slider.featuredEventsCollection = data[2];
            this.slider.render();
            this.render();
            if(!this.collection.length) this.loadNextBatch(); // Automatically extend date range if no events found in first 30 days.
        })
        .catch(err => {
            // TODO: handle error
            this.loadError = true;
            this.render(template`<p>${lang.ErrorLoadingEventData}</p>`);
            console.log('err: ', err)
        });
    }

    /**
     * Fetch this page's data
     */
    fetch() {
        this.loading = true;
        const data = {
            ...this.controls.getFilterOptions(),
            ...this.currentDateRange
        };
        
        if (data.date) {
            // Use date picker date
            data.start = data.date;
            data.end = data.date;
        }

        const promise = api.getEvents(data);

        promise.then(data => {
            this.loading = false;
            this.collection = data;
        })
        .catch(err => {
            // TODO: handle error
            this.loading = false;
        });

        return promise;
    }

    /**
     * Filters out duplicate multi-day events if API includes the same event again in subsequent queries.
     * @param {Object[]} data - Array of event objects
     */
    removeDuplicateEvents(data){
        return data.filter(event => {
            const uniqueEventID = `${event.id}-${event.startDate ?? event.startDatetime}`;
            if(this.eventsSet.has(uniqueEventID)) return false;
            this.eventsSet.add(uniqueEventID);
            return true;
        });
    }

    /**
     * Fetch this page's data and then re-render the event list
     * @param {Object} options - Options passed into renderInto. See ComponentBase.js for documentation on that method
     * @param {String} source - An identifier specifying where refetch was called
     */
    refetch(e, options = {}, source) {
        return this.fetch()
        .then(() => {
            if (this.currentView == 'grid') {
                Emitters.dispatchFetchComplete(this.eventsGrid.el, this.collection, false);    // Forward events to grid view
                setTimeout(() => Emitters.dispatchConvertIcons(this.el), 1);
            } else {
                this.collection = this.removeDuplicateEvents(this.collection);
                let contents = template`${this.eventsTemplate()}`;
                const filterOptions = this.controls.getFilterOptions();

                // Don't display the 'No events found' message when lazy loading
                if (source == 'lazyLoad' && this.collection.length == 0) {
                    contents = '';
                }

                this.renderInto(
                    contents,
                    this.elements.eventWrapper,
                    {
                        renderDone: () => {
                            Emitters.dispatchConvertIcons(this.el);
                            if(filterOptions.featured || filterOptions.query || filterOptions.date || filterOptions.tag.length) {
                                this.slider.hide();
                            } else {
                                this.slider.show(); // Only show slider when no filters are being applied
                            }
                        },
                        ...options
                    }
                );
            }
        });
    }

    afterRender() {
        // Data failed to load, don't try continuing with render here
        if (this.loadError) return;

        this.elements = {
            eventWrapper: this.el.querySelector('.event-wrapper'),
            gridWrapper: this.el.querySelector('.grid-wrapper'),
            loadEventsWrapper: this.el.querySelector('.load-more-events-wrapper'),
            loadEventsMessage: this.el.querySelector('.load-more-events-message'),
            loadEventsButton: this.el.querySelector('.load-more-events'),
        };
        api.setPageTitle(); // restores original page title
        api.setPageDescription(); // restores original page description
        api.setPageCanonical(); // restores original page canonical

        if(vars.pubcalendar.autoscroll) {
            this.addEventListener(window, 'scroll', () => {
                this.checkLazyLoad();
            });
        }

        if (this.defaultLayout == 'GRID') {
            this.showGrid();
        } else {
            this.showList(null, false);
        }
    }

    handleFilterChange() {
        this.elements.loadEventsMessage.innerText = '';

        // Remove filters from URL 
        this.triggerRouteChange(vars.pubcalendar.basepath ?? window.location.pathname, 'replace');

        // Hide 'Load more events' button when viewing a single day
        const filterOptions = this.controls.getFilterOptions();
        this.updateRSSLink(filterOptions);
        if (filterOptions.date) {
            this.elements.loadEventsWrapper.classList.add('hide');
        }
        else if (this.currentView == 'list') {
            this.elements.loadEventsWrapper.classList.remove('hide');
            this.elements.loadEventsButton.classList.remove('hide');

        }

        if (this.currentView == 'list') {
            this.resetCurrentDateRange();
            if(filterOptions.featured) this.slider.hide();
        }

        this.refetch().then(() => {
            if(this.currentView == 'list' && !this.collection.length) this.loadNextBatch(); // Automatically extend date range if no events found in first 30 days.
        });
    }

    updateRSSLink(filterOptions){
        this.el.querySelector('#rss-link')?.setAttribute('href', api.getRSSURL(filterOptions));
    }

    resetCurrentDateRange() {
        const date = DateTime.now();
        this.eventsSet.clear();
        this.currentDateRange = {
            start: date.toISODate(),
            end: date.plus({ days: 30 }).toISODate()
        };
    }

    loadNextBatch(evt) {
        if(this.currentView != 'list') return;
        if(evt) this.loadMoreButtonClicked = true;
        // Get the next 100 days of events
        const date = DateTime.fromISO(this.currentDateRange.end).plus({ days: 1 });
        this.currentDateRange.start = date.toISODate();
        this.currentDateRange.end = date.plus({ days: 100 });

        if(this.currentDateRange.end > this.maxQueryDate){
            this.elements.loadEventsMessage.innerText = this.loadMoreButtonClicked ? lang.NoMoreEvents : ''; // Only show message if user manually clicked load more button
            this.loadMoreButtonClicked = false;
            this.elements.loadEventsButton.classList.add('hide');
            return; // 2 year limit reached so break recursive loop
        }

        this.currentDateRange.end = this.currentDateRange.end.toISODate();

        this.refetch(null, {
            replace: false
        },
        'lazyLoad')
        .then(() => {
            if (this.collection.length) {
                this.elements.eventWrapper.parentElement.querySelector('.event-wrapper > p')?.remove();  // "no events found" message can be in event wrapper when returning from grid view. Remove it if there's events
                this.elements.loadEventsMessage.innerText = '';
            }
            if(this.collection.length < 10) this.loadNextBatch(); // recursively call this method until 10 or more events are returned or 2 years are queried.
        });
    }

    checkLazyLoad() {
        /* Don't try to load more if
            -alreay loading
            -there aren't more events to show
            -currently viewing a single date
            -on the grid view
        */
        if (this.loading || this.collection.length == 0 || this.controls.getFilterOptions().date || this.currentView == 'grid') return;

        const bounds = this.elements.eventWrapper.getBoundingClientRect();
        const offset = 300;     // how far from the bottom of the events list to load in more events
        if (window.scrollY + window.innerHeight >= bounds.bottom + window.scrollY - offset) {
            this.loadNextBatch();
        }
    }

    getDefaultLayout() {
        // Only allow grid layout for devices that have a screen width greater than 960 px. Anything under that starts to get a little cramped.
        return vars.pubcalendar.layout == 'GRID' && window.innerWidth > 960 ? 'GRID' : 'LIST';
    }

    destroy() {
        this.hero.destroy();
        this.controls.destroy();
        this.slider.destroy();
        this.eventsGrid.destroy();
        super.destroy();
    }
}